import React, {MutableRefObject, useEffect, useRef, useState} from "react";
import {Modal, Pressable, Platform, useWindowDimensions, ActivityIndicator} from 'react-native';
import MainStyle from "../../MainStyle";
import * as faceapi from 'face-api.js';
import {Camera, CameraType} from 'expo-camera';
import {Ionicons} from '@expo/vector-icons';
import {colors} from "../assets/styles/colors";
import {detectHeadTurn} from "../utils";
import Div from "../lib/Div";
import P from "../lib/P";

type Props = {
  cameraModalVisible: boolean, onRequestClose: (() => void), takePicture: () => void,
  videoRef: MutableRefObject<Camera | null>, type: 'photo' | 'video'
};

const CameraModal = (props: Props) => {
  const {cameraModalVisible, videoRef, type} = props;
  const [face, setFace] = useState<faceapi.FaceDetection | undefined>();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const video = useRef<HTMLVideoElement | null>(null);
  const {width, height} = useWindowDimensions();
  const [isCameraReady, setIsCameraReady] = useState(false);

  const getAspectRatio = () => {
    return width > height ? 16 / 9 : 4 / 3;
  };

  const startVideo = () => {
    if (Platform.OS === 'web') {
      video.current = document.getElementsByTagName('video')[0];

      if (video.current) {
        if (type === 'video') {
          intervalRef.current = setInterval(async () => {
            detectHeadTurn(video.current).then(direction => {
              if (direction === 'left') console.log('left');
              else if (direction === 'right') console.log('right');
              else console.log('straight');
            });
          }, 100);

        } else {
          intervalRef.current = setInterval(async () => {
            const options = new faceapi.TinyFaceDetectorOptions({inputSize: 320, scoreThreshold: 0.7});
            const detection = await faceapi.detectSingleFace(video.current!, options);
            setFace(detection);
          }, 100);
        }
      }
    }
  };

  const stopVideo = () => {
    if (video.current && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (isCameraReady) startVideo();
  }, [isCameraReady]);

  useEffect(() => {
    if (!cameraModalVisible && isCameraReady) {
      stopVideo();
      setIsCameraReady(false);
    }
  }, [cameraModalVisible, isCameraReady]);

  return (
    <Modal animationType="slide" visible={cameraModalVisible} onRequestClose={props.onRequestClose}>
      <Div bg="black" flex={1} p={10}>
        <Div column rounded={40} overflow="hidden" position="relative" flex={1} mb={10}>
          <Ionicons
            name="close-circle" size={40} color="white" style={[MainStyle.m_t_20, MainStyle.m_l_21]}
            onPress={props.onRequestClose}
          />

          <Camera
            type={CameraType.front} ref={videoRef} style={[...styles.camera, {aspectRatio: getAspectRatio()}]}
            ratio="4:3" onCameraReady={() => setIsCameraReady(true)}
          />

          <Div flex={1} alignItems="center" justifyContent="center" position="relative">
            {isCameraReady ? (
              <Div w={250} h={350} rounded={125} borderWidth={5} borderColor={!!face ? 'white' : 'gray'}/>
            ) : (
              <ActivityIndicator color="white" size="large"/>
            )}
          </Div>

          <Div position="relative" alignItems="center">
            {!!face ? (
              <Ionicons
                name="checkmark-circle" size={24} color={colors.activeText}
                style={[MainStyle.absolute, MainStyle.z_i_2, MainStyle.t__28]}
              />
            ) : (
              <Ionicons
                name="close-circle" size={24} color={colors.warningText}
                style={[MainStyle.absolute, MainStyle.z_i_2, MainStyle.t__28]}
              />
            )}

            <Pressable style={[...styles.btn, {backgroundColor: !!face ? 'white' : 'gray'}]} onPress={props.takePicture}
                       disabled={!face}>
              <Div borderWidth={2} flex={1} m={5} rounded={40}/>
            </Pressable>
          </Div>
        </Div>

        <P ta="center" fs={18}>Try shifting angles or moving to a space with better lighting.</P>
      </Div>
    </Modal>
  );
};

const styles = {
  btn: [MainStyle.w_60, MainStyle.h_60, MainStyle.b_r_40, MainStyle.m_b_10],
  camera: [MainStyle.absolute, MainStyle.t_0, MainStyle.l_0, MainStyle.full_h, MainStyle.full_w, MainStyle.z_i__1]
};

export default React.memo(CameraModal);
